'use client';

import { useEffect, useState } from 'react';
import {
  Check, X, MessageSquare, MapPin, Phone, Mail, ExternalLink,
  FileText, UserPlus, CalendarPlus, Send, Repeat, Plus, AlertTriangle,
} from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import ScheduleDatePicker from '@/components/inbox/ScheduleDatePicker';
import { useAuth } from '@/lib/auth-context';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3003';
const COPILOT_BASE = 'https://secure.copilotcrm.com';

// Shape returned by any *_draft tool. Each kind populates a different
// subset of the payload fields. The executor builds this; keep in sync
// with server/lib/tool-executor.js.
export interface DraftCardData {
  draft_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'sent' | 'failed';
  kind: 'sms_send' | 'email_send' | 'estimate_create' | 'customer_create' | 'visit_create' | 'service_create';
  summary: string;
  payload: {
    // Shared customer-card fields (sms / email / estimate / visit)
    customerId?: string;
    customerName?: string;
    phone?: string;
    email?: string;
    address?: string;

    // Multi-candidate picker (sms / email — wherever name disambiguation matters)
    totalMatches?: number;
    truncated?: boolean;
    candidates?: Array<{
      customerId: string;
      name: string;
      phone: string;
      email: string;
      address: string;
    }>;

    // sms_send
    body?: string;

    // email_send
    templateId?: string;
    templateName?: string;
    subject?: string;
    estimateId?: string | null;
    invoiceId?: string | null;

    // estimate_create
    propertyId?: string | null;
    sqft?: number | null;
    notes?: string;
    lines?: Array<{
      serviceId?: string | null;
      serviceName: string;
      inputName?: string;
      price: number;
      resolved?: boolean;
    }>;
    total?: number;
    copilotLinkPreview?: string;

    // customer_create
    firstName?: string;
    lastName?: string;
    fullName?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    type?: string;

    // visit_create
    service?: string;
    serviceInput?: string;
    date?: string;
    time?: string;
    employees?: Array<{ name: string; id: string | null; resolved: boolean }>;
    recurrence?: string;

    // service_create — catalog addition. type is shared with customer_create
    // (just a string label in both cases). name/price/category/description
    // are unique to service_create.
    name?: string;
    price?: number;
    category?: string;
    description?: string;
  };
}

interface Props {
  data: DraftCardData;
  onResolved?: (next: { status: string; result?: unknown }) => void;
}

const KIND_META: Record<DraftCardData['kind'], { label: string; Icon: typeof MessageSquare }> = {
  sms_send:        { label: 'Draft text',          Icon: MessageSquare },
  email_send:      { label: 'Draft email',         Icon: Send },
  estimate_create: { label: 'Draft estimate',      Icon: FileText },
  customer_create: { label: 'Draft new customer',  Icon: UserPlus },
  visit_create:    { label: 'Draft visit',         Icon: CalendarPlus },
  service_create:  { label: 'Draft new service',   Icon: Plus },
};

function fmtUSD(n: number | undefined | null): string {
  if (typeof n !== 'number' || !isFinite(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function DraftCard({ data, onResolved }: Props) {
  const { tenant } = useAuth();
  const tenantId = tenant?.id || null;
  const [status, setStatus] = useState(data.status);
  const [pickedCustomerId, setPickedCustomerId] = useState<string | null>(
    !data.payload.candidates && data.payload.customerId ? data.payload.customerId : null
  );
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultText, setResultText] = useState<string | null>(null);

  // Editable overrides for kinds where the operator can fix the agent's
  // pick before approving. Currently visit_create supports date / time /
  // notes; future kinds can join this state.
  const [editDate, setEditDate]   = useState<string>(data.payload.date  || '');
  const [editTime, setEditTime]   = useState<string>(data.payload.time  || '');
  const [editNotes, setEditNotes] = useState<string>(data.payload.notes || '');

  const isMulti = Array.isArray(data.payload.candidates) && data.payload.candidates.length > 1;
  const meta = KIND_META[data.kind] || KIND_META.sms_send;
  const KindIcon = meta.Icon;

  // Visit drafts gate Approve on a non-empty date so we never call the
  // approve endpoint with a missing required field.
  const visitMissingDate = data.kind === 'visit_create' && !editDate;

  // On mount, re-check the live status of the draft. The chat history
  // pull rehydrates cards from agent_messages.metadata.tools_used, which
  // captures the status as it was AT STAGE TIME (always 'pending'). If
  // the operator (or another flow) has since approved / declined / let
  // it expire, the card was sticking in pending and Approve would 409
  // with "draft is rejected; can only approve from pending." Refresh
  // here so chat history reflects truth.
  useEffect(() => {
    if (!data.draft_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/api/agent-drafts/${data.draft_id}`);
        if (!res.ok) return;
        const body = await res.json();
        if (cancelled) return;
        if (body && typeof body.status === 'string' && body.status !== status) {
          setStatus(body.status);
        }
      } catch {
        // network blip — leave the card as-is
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.draft_id]);

  async function approve() {
    if (busy || status !== 'pending') return;
    if (isMulti && !pickedCustomerId) {
      setError('Pick a customer first.');
      return;
    }
    if (visitMissingDate) {
      setError('Pick a date before approving.');
      return;
    }
    setBusy('approve');
    setError(null);
    // Build the override body. Multi-customer pickers send the picked
    // customerId; visit drafts send any edited date/time/notes so the
    // approve endpoint can fix the agent's pick before firing.
    const approveBody: Record<string, unknown> = {};
    if (isMulti) approveBody.customerId = pickedCustomerId;
    if (data.kind === 'visit_create') {
      if (editDate  !== (data.payload.date  || '')) approveBody.date  = editDate;
      if (editTime  !== (data.payload.time  || '')) approveBody.time  = editTime;
      if (editNotes !== (data.payload.notes || '')) approveBody.notes = editNotes;
    }
    try {
      const res = await authFetch(`${API_URL}/api/agent-drafts/${data.draft_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(approveBody),
      });
      const body = await res.json();
      if (!res.ok) {
        // 409 invalid_status with a fresh status hint means the draft
        // was already resolved elsewhere (inbox decline, expiry, etc.).
        // Transition the card to that status so the operator can see
        // what really happened. On `failed`, ALSO surface an explanatory
        // message — without it, the buttons silently disappear and the
        // operator reads that as "it worked" (4/30 forensic sweep).
        if (res.status === 409 && body && typeof body.message === 'string') {
          const m = body.message.match(/Draft is (\w+)/);
          if (m && ['rejected', 'sent', 'expired', 'failed'].includes(m[1])) {
            setStatus(m[1] as typeof status);
            if (m[1] === 'failed') {
              setError('This draft already failed. Ask the agent to stage a fresh one to retry.');
            }
            setBusy(null);
            return;
          }
        }
        // 502 send_failed = approval recorded, but the underlying CLI
        // call (Copilot write, etc.) errored. Flip status to 'failed' so
        // the buttons stop rendering and the header shows the red badge.
        // Otherwise the card stays in 'pending' state, the operator
        // clicks Approve again, hits 409 invalid_status, and the buttons
        // silently disappear — reading as "it worked" when it didn't.
        if (res.status === 502 && body && (body.error === 'send_failed' || body.message)) {
          setStatus('failed');
          setError(body.message || body.error || 'The action failed. Ask the agent to stage a fresh draft to retry.');
          setBusy(null);
          return;
        }
        // 400 no_property = customer is missing both a property record
        // AND a usable address, so ensurePropertyId can't auto-create one.
        // Same UX as send_failed: stop rendering buttons (the address
        // problem isn't fixable by clicking Approve again), show the
        // helpful error inline, surface the red header badge so the
        // operator can see at a glance the card is stuck.
        if (res.status === 400 && body && body.error === 'no_property') {
          setStatus('failed');
          setError(body.message || 'This customer has no address on file. Add their address in Copilot, then ask the agent to re-stage.');
          setBusy(null);
          return;
        }
        setError(body.message || body.error || 'Approve failed.');
        setBusy(null);
        return;
      }
      setStatus('sent');
      // Kind-specific success message; falls back to generic.
      let okMsg = 'Done.';
      if (data.kind === 'sms_send') {
        const phone = body?.draft?.payload?.phone || data.payload.phone || '';
        okMsg = phone ? `Sent to ${phone}.` : 'Sent.';
      } else if (data.kind === 'email_send') {
        okMsg = 'Email sent.';
      } else if (data.kind === 'estimate_create') {
        const estId = body?.result?.estimateId || body?.result?.id;
        okMsg = estId ? `Estimate #${estId} created.` : 'Estimate created.';
      } else if (data.kind === 'customer_create') {
        okMsg = 'Customer created.';
      } else if (data.kind === 'visit_create') {
        okMsg = 'Visit scheduled.';
      }
      setResultText(okMsg);
      onResolved?.({ status: 'sent', result: body.result });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed.');
    } finally {
      setBusy(null);
    }
  }

  async function reject() {
    if (busy || status !== 'pending') return;
    setBusy('reject');
    setError(null);
    try {
      const res = await authFetch(`${API_URL}/api/agent-drafts/${data.draft_id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await res.json();
      if (!res.ok) {
        if (res.status === 409 && body && typeof body.message === 'string') {
          const m = body.message.match(/Draft is (\w+)/);
          if (m && ['rejected', 'sent', 'expired', 'failed'].includes(m[1])) {
            setStatus(m[1] as typeof status);
            setBusy(null);
            return;
          }
        }
        setError(body.message || body.error || 'Reject failed.');
        setBusy(null);
        return;
      }
      setStatus('rejected');
      onResolved?.({ status: 'rejected' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed.');
    } finally {
      setBusy(null);
    }
  }

  const isResolved = status !== 'pending';

  // Shared customer card. Used by sms / email / estimate / visit kinds
  // when there is a single resolved customer; the multi-candidate picker
  // replaces it for sms/email when there's name ambiguity.
  function CustomerCard() {
    if (!data.payload.customerName && !data.payload.customerId) return null;
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[13px] font-semibold text-[--text-primary]">
            {data.payload.customerName || `Customer #${data.payload.customerId}`}
          </p>
          {data.payload.customerId && (
            <span className="text-[9px] text-[--text-secondary] font-mono">#{data.payload.customerId}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[--text-secondary]">
          {data.payload.phone   && <span className="inline-flex items-center gap-1"><Phone size={10} />{data.payload.phone}</span>}
          {data.payload.email   && <span className="inline-flex items-center gap-1"><Mail size={10} />{data.payload.email}</span>}
          {data.payload.address && <span className="inline-flex items-center gap-1"><MapPin size={10} />{data.payload.address}</span>}
          {!data.payload.phone && !data.payload.email && !data.payload.address && (
            <span style={{ color: 'var(--text-secondary)' }}>(cached fields empty — Copilot still has the customer details on file)</span>
          )}
        </div>
        {data.payload.customerId && (
          <a
            href={`${COPILOT_BASE}/customers/details/${data.payload.customerId}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-medium mt-0.5 self-start"
            style={{ color: '#00C2A8' }}
          >
            <ExternalLink size={10} /> View in CRM
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border overflow-hidden mb-1.5"
      style={{ background: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: 'var(--border-primary)', background: 'rgba(0, 194, 168, 0.06)' }}
      >
        <KindIcon size={14} style={{ color: '#00C2A8' }} />
        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#00C2A8' }}>
          {meta.label}{isMulti ? ' — pick recipient' : ''}
        </span>
        {status === 'sent' && (
          <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
            done
          </span>
        )}
        {status === 'rejected' && (
          <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(148,163,184,0.18)', color: '#64748b' }}>
            declined
          </span>
        )}
        {status === 'failed' && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
            <AlertTriangle size={10} /> failed
          </span>
        )}
        {status === 'expired' && (
          <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(148,163,184,0.18)', color: '#64748b' }}>
            expired
          </span>
        )}
      </div>

      <div className="px-3 py-3 space-y-2.5">
        {/* Multi-candidate picker (sms_send / email_send only — kinds that
            disambiguate by customer name). Replaces the single-customer
            card. */}
        {isMulti && (
          <div className="space-y-1.5">
            <p className="text-[11px] text-[--text-secondary]">
              {data.payload.truncated && data.payload.totalMatches
                ? `Showing top ${data.payload.candidates!.length} of ${data.payload.totalMatches} matches. Tap one to pick the recipient:`
                : `Found ${data.payload.candidates!.length} matches. Tap one to pick the recipient:`}
            </p>
            <div className="space-y-1">
              {data.payload.candidates!.map(c => {
                const picked = pickedCustomerId === c.customerId;
                const hasContactInfo = !!(c.phone || c.email || c.address);
                return (
                  <div
                    key={c.customerId}
                    className="rounded-lg border transition-colors overflow-hidden"
                    style={{
                      borderColor: picked ? '#00C2A8' : 'var(--border-primary)',
                      background: picked ? 'rgba(0,194,168,0.10)' : 'transparent',
                    }}
                  >
                    <button
                      onClick={() => !isResolved && setPickedCustomerId(c.customerId)}
                      disabled={isResolved}
                      className="w-full flex flex-col items-start text-left px-2.5 py-1.5"
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <span className="text-[13px] font-medium text-[--text-primary]">{c.name}</span>
                        <span className="text-[9px] text-[--text-secondary] font-mono">#{c.customerId}</span>
                      </div>
                      {hasContactInfo ? (
                        <span className="text-[10px] text-[--text-secondary] flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                          {c.phone   && <span className="inline-flex items-center gap-0.5"><Phone size={9} />{c.phone}</span>}
                          {c.email   && <span className="inline-flex items-center gap-0.5 truncate max-w-[180px]"><Mail size={9} />{c.email}</span>}
                          {c.address && <span className="inline-flex items-center gap-0.5 truncate max-w-[200px]"><MapPin size={9} />{c.address}</span>}
                        </span>
                      ) : (
                        <span className="text-[10px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          (cached fields empty — Copilot still has the customer details on file)
                        </span>
                      )}
                    </button>
                    <a
                      href={`${COPILOT_BASE}/customers/details/${c.customerId}`}
                      target="_blank" rel="noopener noreferrer"
                      className="block px-2.5 py-1 text-[10px] font-medium border-t hover:bg-[--bg-main]"
                      style={{ borderColor: 'var(--border-primary)', color: '#00C2A8' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink size={10} /> View in CRM
                      </span>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Single-customer card (when not in multi-candidate mode and the
            kind has a target customer to display). customer_create has no
            "target customer" — it IS the customer creation. service_create
            adds a catalog item, not tied to a customer. */}
        {!isMulti && data.kind !== 'customer_create' && data.kind !== 'service_create' && <CustomerCard />}

        {/* Kind-specific body. */}
        {data.kind === 'sms_send' && (
          <div
            className="rounded-lg px-2.5 py-2 text-[12px] text-[--text-primary] whitespace-pre-wrap"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <span className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: 'var(--text-secondary)' }}>
              Message preview
            </span>
            {data.payload.body ? `"${data.payload.body}"` : <span style={{ color: 'var(--text-secondary)' }}>(no body)</span>}
          </div>
        )}

        {data.kind === 'email_send' && (
          <div
            className="rounded-lg px-2.5 py-2 text-[12px] text-[--text-primary] space-y-1"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <span className="text-[10px] uppercase tracking-wide block" style={{ color: 'var(--text-secondary)' }}>
              Template
            </span>
            <p className="font-medium">{data.payload.templateName || `Template #${data.payload.templateId}`}</p>
            {data.payload.subject && (
              <>
                <span className="text-[10px] uppercase tracking-wide block mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Subject override
                </span>
                <p className="text-[11px] text-[--text-secondary]">{data.payload.subject}</p>
              </>
            )}
            {(data.payload.estimateId || data.payload.invoiceId) && (
              <p className="text-[10px] text-[--text-secondary] mt-1">
                Attached to {data.payload.estimateId ? `estimate #${data.payload.estimateId}` : `invoice #${data.payload.invoiceId}`}
              </p>
            )}
          </div>
        )}

        {data.kind === 'estimate_create' && (
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <div className="px-2.5 py-1.5 border-b text-[10px] uppercase tracking-wide" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>
              Line items
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
              {(data.payload.lines || []).map((l, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-2.5 py-1.5 text-[12px]">
                  <div className="flex flex-col">
                    <span className="text-[--text-primary]">{l.serviceName}</span>
                    {l.resolved === false && (
                      <span className="text-[9px]" style={{ color: '#f59e0b' }}>
                        ⚠ service name not matched in Copilot — Approve will fail
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-[--text-primary]">{fmtUSD(l.price)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-2.5 py-2 border-t text-[12px] font-semibold" style={{ borderColor: 'var(--border-primary)' }}>
              <span>Total</span>
              <span className="font-mono" style={{ color: '#00C2A8' }}>{fmtUSD(data.payload.total)}</span>
            </div>
            {data.payload.notes && (
              <div className="px-2.5 py-1.5 text-[11px] border-t" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>
                <span className="text-[10px] uppercase tracking-wide block mb-0.5">Notes</span>
                {data.payload.notes}
              </div>
            )}
          </div>
        )}

        {data.kind === 'customer_create' && (
          <div
            className="rounded-lg px-2.5 py-2 text-[12px] space-y-1"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <p className="font-semibold text-[13px] text-[--text-primary]">
              {data.payload.fullName || data.payload.firstName || '(unnamed)'}
              {data.payload.type && <span className="text-[10px] font-normal ml-2 px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,194,168,0.10)', color: '#00C2A8' }}>{data.payload.type}</span>}
            </p>
            <div className="text-[11px] text-[--text-secondary] flex flex-col gap-0.5">
              {data.payload.phone   && <span className="inline-flex items-center gap-1"><Phone size={10} />{data.payload.phone}</span>}
              {data.payload.email   && <span className="inline-flex items-center gap-1"><Mail size={10} />{data.payload.email}</span>}
              {data.payload.address && <span className="inline-flex items-center gap-1"><MapPin size={10} />{data.payload.address}</span>}
            </div>
          </div>
        )}

        {data.kind === 'service_create' && (
          <div
            className="rounded-lg px-2.5 py-2 text-[12px] space-y-1.5"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-semibold text-[13px] text-[--text-primary]">
                {data.payload.name || '(unnamed service)'}
              </p>
              <span className="font-mono text-[13px] font-semibold" style={{ color: '#00C2A8' }}>
                {fmtUSD(data.payload.price)}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[--text-secondary]">
              {data.payload.type     && <span>Type: {data.payload.type}</span>}
              {data.payload.category && <span>Category: {data.payload.category}</span>}
            </div>
            {data.payload.description && (
              <p className="text-[11px] text-[--text-secondary] whitespace-pre-wrap">{data.payload.description}</p>
            )}
          </div>
        )}

        {data.kind === 'visit_create' && (
          <div
            className="rounded-lg px-2.5 py-2 text-[12px] space-y-2"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-semibold text-[13px] text-[--text-primary]">{data.payload.service}</p>
              {data.payload.recurrence && data.payload.recurrence !== 'none' && (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,194,168,0.10)', color: '#00C2A8' }}>
                  <Repeat size={9} />{data.payload.recurrence}
                </span>
              )}
            </div>

            {/* Editable date + time pair. Date is required; time is optional.
                Stacks on phones, pairs on ≥sm. Inputs are 16px font to
                avoid iOS Safari auto-zoom on focus. The explicit showPicker()
                onClick / onFocus hooks are belt-and-suspenders for browsers
                that don't open the native picker on bare click (Firefox, some
                desktop Edge configs). Native iOS / Android handle it natively
                without needing the call. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex flex-col gap-0.5 cursor-pointer">
                <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Date</span>
                {isResolved ? (
                  <span className="text-xs text-[--text-secondary]">{editDate}</span>
                ) : (
                  <ScheduleDatePicker tenantId={tenantId} value={editDate || null} onChange={setEditDate} />
                )}
              </label>
              <label className="flex flex-col gap-0.5 cursor-pointer">
                <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Time (optional)</span>
                <input
                  type="time"
                  value={editTime}
                  onChange={e => setEditTime(e.target.value)}
                  disabled={isResolved}
                  onClick={e => { try { (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                  onFocus={e => { try { (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                  className="w-full rounded-md px-3 py-2 text-[16px] text-[--text-primary] focus:outline-none focus:ring-2 focus:ring-[#00C2A8]/30 disabled:opacity-60 cursor-pointer min-h-[44px]"
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)' }}
                />
              </label>
            </div>

            {Array.isArray(data.payload.employees) && data.payload.employees.length > 0 && (
              <div className="text-[11px] text-[--text-secondary]">
                Crew: {data.payload.employees.map(e => e.name + (e.resolved ? '' : ' ⚠')).join(', ')}
              </div>
            )}
            {data.payload.employees && data.payload.employees.some(e => !e.resolved) && (
              <p className="text-[10px]" style={{ color: '#f59e0b' }}>
                ⚠ At least one employee name didn&apos;t match — Approve will fail. Ask the agent to pick a different employee by ID.
              </p>
            )}

            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Notes (optional)</span>
              <textarea
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                disabled={isResolved}
                rows={2}
                placeholder="Add a note for the crew..."
                className="w-full rounded-md px-3 py-2 text-[16px] text-[--text-primary] focus:outline-none focus:ring-2 focus:ring-[#00C2A8]/30 resize-none disabled:opacity-60"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)' }}
              />
            </label>
          </div>
        )}

        {error && <p className="text-[11px]" style={{ color: '#ef4444' }}>{error}</p>}
        {resultText && <p className="text-[11px]" style={{ color: '#22c55e' }}>{resultText}</p>}

        {!isResolved && (
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={approve}
              disabled={!!busy || (isMulti && !pickedCustomerId) || visitMissingDate}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-[14px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
              style={{ background: '#00C2A8' }}
              title={visitMissingDate ? 'Pick a date first' : undefined}
            >
              <Check size={15} />
              {busy === 'approve' ? 'Working...' : 'Approve'}
            </button>
            <button
              onClick={reject}
              disabled={!!busy}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-[14px] font-medium text-[--text-secondary] hover:text-[--text-primary] transition-colors disabled:opacity-50 min-h-[44px]"
              style={{ border: '1px solid var(--border-primary)' }}
            >
              <X size={15} />
              {busy === 'reject' ? 'Declining...' : 'Decline'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
